import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react'
import AnimatedSection from '@/components/AnimatedSection'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { useReviews } from '@/hooks/useReviews'

// Deterministic gradient based on name
function avatarGradient(name: string) {
  const gradients = [
    'from-amber-700 to-yellow-500',
    'from-neutral-800 to-neutral-600',
    'from-stone-700 to-stone-500',
    'from-yellow-700 to-amber-500',
    'from-zinc-800 to-zinc-500',
    'from-orange-800 to-amber-500',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  const index = Math.abs(hash) % gradients.length
  return gradients[index]
}

export default function Testimonials() {
  const { reviews, isLoading } = useReviews()
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    slidesToScroll: 1,
  })

  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  // Autoplay
  useEffect(() => {
    if (!emblaApi) return
    const interval = setInterval(() => {
      emblaApi.scrollNext()
    }, 5000)
    return () => clearInterval(interval)
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  )

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  return (
    <section className="bg-white py-24">
      <div className="section-padding mx-auto max-w-7xl">
        <AnimatedSection className="mb-14 max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">
            Testimonials
          </span>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-black sm:text-4xl">
            What Our Clients Say
          </h2>
          <p className="mt-5 text-sm leading-7 text-neutral-600">
            Don't just take our word for it. Here's what industry leaders say about
            working with Advict Spatial Consult.
          </p>
        </AnimatedSection>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {reviews.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="flex-[0_0_100%] md:flex-[0_0_50%] min-w-0"
                >
                  <div className="relative h-full border border-neutral-200 bg-white p-8">
                    <Quote className="w-10 h-10 text-accent/30 absolute top-6 right-6" />
                    <div className="flex gap-1 mb-4">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 fill-accent text-accent"
                        />
                      ))}
                    </div>
                    <p className="text-neutral-700 leading-relaxed mb-6 text-sm">
                      "{testimonial.content}"
                    </p>
                    <div className="flex items-center gap-4">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${avatarGradient(testimonial.name)} shadow-inner`}>
                        <span className="text-white font-bold text-lg">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-black">
                          {testimonial.name}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {testimonial.role}, {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          {reviews.length > 1 && <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 lg:-translate-x-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white border border-neutral-200 text-black shadow-sm transition-all hover:bg-accent hover:text-black hover:border-accent"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>}
          {reviews.length > 1 && <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 lg:translate-x-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white border border-neutral-200 text-black shadow-sm transition-all hover:bg-accent hover:text-black hover:border-accent"
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-5 w-5" />
          </button>}
        </div>

        {/* Carousel Dots */}
        {!isLoading && reviews.length === 0 && <p className="mt-8 text-center text-neutral-500">Client reviews will appear here soon.</p>}
        <div className="flex justify-center gap-2 mt-8">
          {reviews.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                selectedIndex === index
                  ? 'bg-accent w-8'
                  : 'bg-neutral-300 hover:bg-neutral-400'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
